import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import BookingModal from "../components/BookingModal";

function Packages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalPackages, setTotalPackages] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const itemsPerPage = 9; // Load 9 at a time

  // Load initial packages
  const loadPackages = async (page = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const url = `${process.env.REACT_APP_API_URL}/packages?page=${page}&limit=${itemsPerPage}`;
      console.log(`📡 Fetching packages: ${url}`);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log(data.data);


      if (data.pagination) {
        if (append) {
          setPackages(prev => [...prev, ...data.data]);
        } else {
          setPackages(data.data);
        }
        setHasMore(data.pagination.page < data.pagination.totalPages);
        setTotalPackages(data.pagination.total);
        setCurrentPage(data.pagination.page);
      } else if (Array.isArray(data)) {
        setPackages(data);
        setHasMore(false);
        setTotalPackages(data.length);
      }

    } catch (err) {
      console.error("Error fetching packages:", err);
      setError("Unable to load packages. Please try again later.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load more packages
  const loadMore = () => {
    if (!loadingMore && hasMore) {
      loadPackages(currentPage + 1, true);
    }
  };

  useEffect(() => {
    loadPackages(1, false);
  }, []);

  const truncateHtml = (html, maxLength = 120) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    const text = div.textContent || div.innerText || "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  const handleBookNow = (pkg) => {
    setSelectedPackage(pkg);
    setShowModal(true);
  };

  return (
    <>
      <Helmet>
        <title>Tour Packages & Holiday Deals | Primedale Tours & Travel</title>
      </Helmet>

      <div>
        {/* Hero Section */}
        <div className="container-fluid bg-primary py-5 mb-5 hero-header">
          <div className="container py-5">
            <div className="row justify-content-center py-5">
              <div className="col-lg-10 text-center">
                <h1 className="display-3 text-white animated slideInDown">Packages</h1>
                {totalPackages > 0 && (
                  <p className="text-white mt-3">
                    Showing {packages.length} of {totalPackages} amazing packages
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Booking Steps */}
        <div className="container mb-5">
          <div className="text-center mb-4">
            <h2 style={{ color: "#ff831d" }}>How To Book</h2>
            <p className="text-muted">Your journey in 5 simple steps</p>
          </div>
          {/* ... your booking steps code ... */}
        </div>

        {/* Packages Grid */}
        <div className="container-xxl py-5">
          <div className="container">
            <div className="text-center mb-5">
              <h6 className="section-title bg-white text-center px-3">Packages</h6>
              <h1 className="mb-4 or">Awesome Packages</h1>
            </div>

            {loading && <p className="text-center">Loading packages...</p>}
            {error && <p className="text-center text-danger">{error}</p>}

            <div className="row g-4 justify-content-center">
              {!loading &&
                !error &&
                packages.map((pkg) => (
                  <div key={pkg.id} className="col-lg-4 col-md-6">
                    <div className="package-item h-100">
                      <div className="overflow-hidden">
                        <img
                          className="img-fluid"
                          src={pkg.image}
                          alt={pkg.title}
                          loading="lazy"
                          style={{ width: '100%', height: '250px', objectFit: 'cover' }}
                        />
                      </div>

                      <div className="d-flex border-bottom">
                        <small className="flex-fill text-center py-2">
                          <i className="icon-color fa fa-map-marker-alt me-2" />
                          {pkg.destination}
                        </small>
                        <small className="flex-fill text-center py-2">
                          <i className="icon-color fa fa-calendar-alt me-2" />
                          {pkg.duration}
                        </small>
                      </div>

                      <div className="text-center p-4">
                        <div className="mb-3">
                          {[...Array(pkg.rating || 5)].map((_, i) => (
                            <small key={i} className="icon-color fa fa-star" />
                          ))}
                        </div>

                        <h5 className="mb-3" style={{ color: "#ff831d" }}>
                          {pkg.title}
                        </h5>

                        <p className="text-muted mb-3">
                          {truncateHtml(pkg.shortDescription || "")}
                        </p>

                        <div className="d-flex justify-content-center mb-2">
                          <Link
                            to={`/package-details/${pkg.slug}`}
                            // state={{ package: pkg }}
                            className="btn btn-sm navyblue-bg px-3"
                            style={{ borderRadius: "30px 0 0 30px" }}
                          >
                            Read More
                          </Link>
                          <button
                            className="btn btn-sm navyblue-bg px-3"
                            style={{ borderRadius: "0 30px 30px 0" }}
                            onClick={() => handleBookNow(pkg)}
                          >
                            Request Quote
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Load More Button */}
            {!loading && hasMore && (
              <div className="text-center mt-5">
                <button
                  className="btn btn-lg px-5 py-3"
                  onClick={loadMore}
                  disabled={loadingMore}
                  style={{
                    backgroundColor: "#ff831d",
                    color: "white",
                    borderRadius: "30px",
                    transition: "all 0.3s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#e67500";
                    e.target.style.transform = "scale(1.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#ff831d";
                    e.target.style.transform = "scale(1)";
                  }}
                >
                  {loadingMore ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Loading...
                    </>
                  ) : (
                    <>
                      Load More Packages
                      <i className="fas fa-arrow-down ms-2"></i>
                    </>
                  )}
                </button>
                <p className="text-muted mt-2">
                  Showing {packages.length} of {totalPackages} packages
                </p>
              </div>
            )}

            {/* Show All Button (Alternative) */}
            {!loading && !hasMore && packages.length < totalPackages && (
              <div className="text-center mt-5">
                <button
                  className="btn btn-outline-primary btn-lg px-5 py-3"
                  onClick={() => loadPackages(1, false)}
                  style={{ borderRadius: "30px" }}
                >
                  <i className="fas fa-sync-alt me-2"></i>
                  Reset View
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Booking Modal */}
        {showModal && selectedPackage && (
          <BookingModal pkg={selectedPackage} onClose={() => setShowModal(false)} />
        )}
      </div>
    </>
  );
}

export default Packages;